'use client'

import { useEffect, useMemo, useState } from 'react'
import { RichText } from '@payloadcms/richtext-lexical/react'
import { CardContent } from '@/components/ui/card'
import { SectionCard } from '@/components/ui/section-card'
import { EventVolunteerHeadcountBadges } from '@/components/events/event-volunteer-headcount'
import { useLoginState } from '@/hooks/use-login-state'
import { isTeamLeaderOrAbove } from '@/lib/intranet-preview-shared'
import type { Event, User } from '@/payload-types'
import { EventTeamLeaderContacts } from './event-team-leader-contacts'

type RichTextValue = Event['volunteer_details']
type LoginRole = NonNullable<User['roles']>[number]
type IntranetRole = NonNullable<User['intranet_roles']>[number]

type VolunteerCounts = {
  confirmed: number
  unconfirmed: number
  needed: number
}

type MemberDetailsResponse = {
  volunteerDetails?: RichTextValue
  teamLeaderDetails?: RichTextValue
  teamLeaderContacts?: ContactEntry[]
  talentRepContacts?: ContactEntry[]
  regionalCoordinatorContacts?: ContactEntry[]
  eventDetailSections?: EventDetailSection[]
  volunteerCounts?: VolunteerCounts | null
}

type ContactEntry = {
  name: string
  email: string
  phone: string | null
  avatarUrl: string | null
  avatarAlt: string | null
  profileImageUrl: string | null
  bio: string | null
}

type EventDetailSection = {
  key: string
  label: string
  data: NonNullable<RichTextValue>
}

type EventMemberDetailsProps = {
  eventId: string
  publicDetails: RichTextValue
  basePath?: string
}

function stripUnsupportedLexicalNodes(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value
      .map((entry) => stripUnsupportedLexicalNodes(entry))
      .filter((entry) => entry !== null)
  }

  if (!value || typeof value !== 'object') {
    return value
  }

  const node = value as Record<string, unknown>
  const nodeType = node.type
  if (nodeType === 'block' || nodeType === 'inlineBlock') {
    return null
  }

  const next: Record<string, unknown> = {}
  for (const [key, child] of Object.entries(node)) {
    if (key === 'children' && Array.isArray(child)) {
      next[key] = child
        .map((entry) => stripUnsupportedLexicalNodes(entry))
        .filter((entry) => entry !== null)
      continue
    }

    next[key] = stripUnsupportedLexicalNodes(child)
  }

  return next
}

function toClientSafeRichText(value: RichTextValue): RichTextValue {
  if (!value) return value
  return stripUnsupportedLexicalNodes(value) as RichTextValue
}

function canSeeTeamLeaderDetails(roles: LoginRole[], intranetRoles: IntranetRole[]): boolean {
  if (roles.includes('admin')) return true
  return intranetRoles.length > 0
}

function canSeeTeamLeaderAndAbove(roles: LoginRole[], intranetRoles: IntranetRole[]): boolean {
  return isTeamLeaderOrAbove(roles, intranetRoles)
}

// Mirrors `hasEventDetailsAccess` in the member-details route: the Venue/Tour/
// Talent "More Info" sections are leadership-only and must never render for
// volunteers or confirmed volunteers.
function canSeeEventDetailSections(roles: LoginRole[], intranetRoles: IntranetRole[]): boolean {
  if (roles.includes('admin') || roles.includes('cms')) return true
  return (
    intranetRoles.includes('team_leader') ||
    intranetRoles.includes('talent_rep') ||
    intranetRoles.includes('regional_coordinator')
  )
}

export function EventMemberDetails({ eventId, publicDetails, basePath = '/events' }: EventMemberDetailsProps) {
  const { isLoading, isLoggedIn, roles, intranetRoles, previewValue } = useLoginState()
  const [details, setDetails] = useState<MemberDetailsResponse | null>(null)

  const hasTeamLeaderAccess = useMemo(
    () => canSeeTeamLeaderDetails(roles, intranetRoles),
    [intranetRoles, roles],
  )
  const hasTeamLeaderAndAboveAccess = useMemo(
    () => canSeeTeamLeaderAndAbove(roles, intranetRoles),
    [intranetRoles, roles],
  )
  const hasEventDetailsAccess = useMemo(
    () => canSeeEventDetailSections(roles, intranetRoles),
    [intranetRoles, roles],
  )

  useEffect(() => {
    if (isLoading || !isLoggedIn) {
      setDetails(null)
      return
    }

    let isCancelled = false

    const loadDetails = async () => {
      try {
        const response = await fetch(`/api/events/${eventId}/member-details`, {
          method: 'GET',
          credentials: 'include',
        })

        if (!response.ok) {
          if (!isCancelled) {
            setDetails(null)
          }
          return
        }

        const payload = (await response.json()) as MemberDetailsResponse
        if (!isCancelled) {
          setDetails(payload)
        }
      } catch {
        if (!isCancelled) {
          setDetails(null)
        }
      }
    }

    void loadDetails()

    return () => {
      isCancelled = true
    }
  }, [eventId, isLoading, isLoggedIn, previewValue])

  const showPublicSection = Boolean(publicDetails)
  const volunteerDetails = details?.volunteerDetails
  const teamLeaderDetails = details?.teamLeaderDetails
  const teamLeaderContacts = details?.teamLeaderContacts || []
  const talentRepContacts = details?.talentRepContacts || []
  const regionalCoordinatorContacts = details?.regionalCoordinatorContacts || []
  const eventDetailSections = hasEventDetailsAccess ? details?.eventDetailSections || [] : []
  const volunteerCounts =
    isTeamLeaderOrAbove(roles, intranetRoles) ? details?.volunteerCounts ?? null : null
  const showVolunteerCounts = volunteerCounts != null
  const safePublicDetails = toClientSafeRichText(publicDetails)
  const safeVolunteerDetails = toClientSafeRichText(volunteerDetails ?? null)
  const safeTeamLeaderDetails = toClientSafeRichText(teamLeaderDetails ?? null)
  const safeEventDetailSections = eventDetailSections
    .map((section) => ({
      ...section,
      data: toClientSafeRichText(section.data) as NonNullable<RichTextValue>,
    }))
    .filter((section) => Boolean(section.data))
  const showVolunteerSection = Boolean(volunteerDetails)
  const showTeamLeaderSection = hasTeamLeaderAccess && Boolean(teamLeaderDetails)
  const showTalentRepContacts = hasTeamLeaderAndAboveAccess && talentRepContacts.length > 0
  const showRegionalCoordinatorContacts =
    hasTeamLeaderAndAboveAccess && regionalCoordinatorContacts.length > 0
  const showMemberDetailsCard =
    showPublicSection ||
    showVolunteerSection ||
    showTeamLeaderSection ||
    showVolunteerCounts ||
    teamLeaderContacts.length > 0 ||
    showTalentRepContacts ||
    showRegionalCoordinatorContacts
  const showEventDetailsSections = safeEventDetailSections.length > 0

  if (!showMemberDetailsCard && !showEventDetailsSections) {
    return null
  }

  return (
    <>
      {showMemberDetailsCard && (
        <SectionCard
          className="mt-6"
          trigger={
            <div>
              <div className="text-xl font-bold">Details</div>
            </div>
          }
          bareContent
          headerDivider={false}
        >
          <CardContent className="divide-y divide-black border-t border-black p-0">
            {showVolunteerCounts && volunteerCounts && (
              <section>
                <div className="border-b border-black bg-neutral-100 p-4 py-1 font-bold">
                  <a href={`${basePath}/${eventId}/volunteers`} className="hover:underline">
                    Volunteers
                  </a>
                </div>
                <div className="flex flex-wrap items-center gap-1.5 p-4 md:px-6 md:py-4">
                  <EventVolunteerHeadcountBadges
                    confirmed={volunteerCounts.confirmed}
                    needed={volunteerCounts.needed}
                    unconfirmed={volunteerCounts.unconfirmed}
                    showUnconfirmed
                  />
                </div>
              </section>
            )}

            {showPublicSection && (
              <section>
                <div className="border-b border-black bg-neutral-100 p-4 py-1 font-bold">Event Details</div>
                <div className="p-4 md:px-6 md:py-4">
                  <RichText
                    data={safePublicDetails as NonNullable<RichTextValue>}
                    className="content-block-text flex w-full flex-col gap-theme-md md:gap-theme-lg"
                  />
                </div>
              </section>
            )}

            <EventTeamLeaderContacts title="Team Leaders" teamLeaders={teamLeaderContacts} />
            {showTalentRepContacts && (
              <EventTeamLeaderContacts title="Talent Rep" teamLeaders={talentRepContacts} />
            )}
            {showRegionalCoordinatorContacts && (
              <EventTeamLeaderContacts
                title="Regional Coordinator"
                teamLeaders={regionalCoordinatorContacts}
              />
            )}

            {showVolunteerSection && (
              <section>
                <div className="border-b border-black bg-neutral-100 p-4 py-1 font-bold">Volunteer Details</div>
                <div className="p-4 md:px-6 md:py-4">
                  <RichText
                    data={safeVolunteerDetails as NonNullable<RichTextValue>}
                    className="content-block-text flex w-full flex-col gap-theme-md md:gap-theme-lg"
                  />
                </div>
              </section>
            )}

            {showTeamLeaderSection && (
              <section>
                <div className="border-b border-black bg-neutral-100 p-4 py-1 font-bold">Team Leader Details</div>
                <div className="p-4 md:px-6 md:py-4">
                  <RichText
                    data={safeTeamLeaderDetails as NonNullable<RichTextValue>}
                    className="content-block-text flex w-full flex-col gap-theme-md md:gap-theme-lg"
                  />
                </div>
              </section>
            )}
          </CardContent>
        </SectionCard>
      )}

      {showEventDetailsSections && (
        <SectionCard
          className="mt-6"
          trigger={
            <div>
              <div className="text-xl font-bold">More Info</div>
            </div>
          }
          bareContent
          headerDivider={false}
        >
          <CardContent className="divide-y divide-black border-t border-black p-0">
            {safeEventDetailSections.map((section) => (
              <section key={section.key}>
                <div className="border-b border-black bg-neutral-100 p-4 py-1 font-bold">
                  {section.label}
                </div>
                <div className="p-4 md:px-6 md:py-4">
                  <RichText
                    data={section.data}
                    className="content-block-text flex w-full flex-col gap-theme-md md:gap-theme-lg"
                  />
                </div>
              </section>
            ))}
          </CardContent>
        </SectionCard>
      )}
    </>
  )
}
